<script setup>
import { ref, computed } from 'vue'
import StatusPill from '@/components/shell/StatusPill.vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import { store } from '@/store/appStore.js'
import { STAGES, tatSince, formatTat } from '@/data/shared/orderModel.js'
import { computeLineValue, getProduct } from '@/data/shared/products.js'

const search = ref(''); const statusFilter = ref('All')
const drawerOrderNo = ref(null)
const orders = computed(() => store.salesExecV2.orders.value)
const pharmacies = computed(() => store.salesExecV2.pharmacies.value)
const products = store.salesExecV2.products
const schemeProducts = computed(() => products.filter(p => p.schemeEligible))
const nonSchemeProducts = computed(() => products.filter(p => !p.schemeEligible))
// Prevent the same product being picked on two different lines of one order
// — that was the real cause of the "changing one line's qty changes
// another's" bug: Telecaller/Warehouse key their per-line inputs by SKU, and
// two lines sharing a SKU means they share that key. Each line's own current
// selection stays visible in its own dropdown; it's only excluded from the
// OTHER lines' options.
function usedSkusExcept(index) {
  return new Set(form.value.lines.filter((_, i) => i !== index).map(l => l.productSku).filter(Boolean))
}
function availableSchemeProducts(index) {
  const used = usedSkusExcept(index)
  return schemeProducts.value.filter(p => !used.has(p.sku))
}
function availableNonSchemeProducts(index) {
  const used = usedSkusExcept(index)
  return nonSchemeProducts.value.filter(p => !used.has(p.sku))
}

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return orders.value.filter(o => {
    const matchesQ = !q || o.orderNo.toLowerCase().includes(q) || o.pharmacyName.toLowerCase().includes(q)
    const matchesStatus = statusFilter.value === 'All' || o.currentStage === statusFilter.value
    return matchesQ && matchesStatus
  }).sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate))
})
const statusOptions = computed(() => ['All', ...new Set(orders.value.map(o => o.currentStage))])
const drawerOrder = computed(() => drawerOrderNo.value ? orders.value.find(o => o.orderNo === drawerOrderNo.value) || null : null)
const stageIdx = computed(() => drawerOrder.value ? STAGES.indexOf(drawerOrder.value.currentStage) : -1)
const isLateral = computed(() => drawerOrder.value && !STAGES.includes(drawerOrder.value.currentStage))
function openDrawer(o) { drawerOrderNo.value = o.orderNo }

// --- New Order form ------------------------------------------------------
const showNewOrder = ref(false)
const form = ref({ customerId: '', lines: [{ productSku: '', quantity: 10 }] })
function openNewOrder() { form.value = { customerId: '', lines: [{ productSku: '', quantity: 10 }] }; showNewOrder.value = true }
function addLine() { form.value.lines.push({ productSku: '', quantity: 10 }) }
function removeLine(i) { form.value.lines.splice(i, 1) }

const selectedPharmacy = computed(() => pharmacies.value.find(p => p.customerId === form.value.customerId))
// Point 15: just the percentage, no elaborate override flow or explanatory message.
const creditUsedPct = computed(() => {
  if (!selectedPharmacy.value) return null
  const bp = store.salesExecV2.businessPotential.value.rows.find(p => p.customerId === selectedPharmacy.value.customerId)
  if (!bp || !bp.potentialValue) return 0
  return Math.min(100, Math.round((bp.actual / bp.potentialValue) * 100))
})
function lineDetail(line) {
  if (!line.productSku || !line.quantity) return null
  return computeLineValue(line.productSku, line.quantity)
}
// Live indicator: shows as soon as a scheme-eligible product is picked,
// independent of the current quantity, so the exec knows the offer exists
// before they even decide how many to order.
function productHasScheme(sku) {
  const p = getProduct(sku)
  return !!(p && p.schemeEligible)
}
const orderTotal = computed(() => form.value.lines.reduce((sum, l) => { const d = lineDetail(l); return sum + (d ? d.value : 0) }, 0))
const canSubmit = computed(() => form.value.customerId && form.value.lines.every(l => l.productSku && l.quantity > 0))

const lastOtp = ref(null)
const otpEntry = ref('')
const otpError = ref('')
function placeOrder() {
  if (!canSubmit.value) return
  const created = store.salesExecV2.createOrder({ customerId: form.value.customerId, lineItems: form.value.lines })
  if (created) {
    // Point 1: the MR can't self-confirm — the order parks at the OTP gate
    // until the pharmacy confirms. Surfacing the code here (and letting it
    // be entered right here) because there's no SMS gateway in the
    // prototype and no separate pharmacy-side screen active in this demo.
    lastOtp.value = { orderNo: created.orderNo, code: created.otpCode, sentTo: created.otpSentTo }
    otpEntry.value = ''
    otpError.value = ''
    showNewOrder.value = false
  }
}
function submitOtpEntry() {
  if (!lastOtp.value) return
  const res = store.salesExecV2.confirmOtp(lastOtp.value.orderNo, otpEntry.value)
  if (!res.ok) { otpError.value = res.reason === 'wrong-code' ? 'Incorrect OTP' : 'Order no longer awaiting OTP'; return }
  lastOtp.value = null
  otpEntry.value = ''
  otpError.value = ''
}
function dismissOtpBanner() { lastOtp.value = null }
</script>

<template>
  <div>
    <div class="card module-header" style="display:flex;justify-content:space-between;align-items:flex-end;gap:12px;flex-wrap:wrap;">
      <h2>{{ orders.length }} orders</h2>
      <button class="btn-primary" @click="openNewOrder">+ New Order</button>
    </div>

    <div v-if="lastOtp" class="card" style="padding:16px 18px;margin-bottom:16px;border-left:3px solid var(--color-accent);">
      <div style="margin-bottom:10px;">
        <strong>{{ lastOtp.orderNo }}</strong> raised — OTP <strong style="letter-spacing:3px;">{{ lastOtp.code }}</strong> sent to {{ lastOtp.sentTo }} for pharmacy confirmation.
      </div>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
        <input class="form-input" style="width:140px;" maxlength="4" v-model="otpEntry" placeholder="Enter OTP" @keyup.enter="submitOtpEntry" />
        <button class="btn-primary" :disabled="otpEntry.trim().length !== 4" @click="submitOtpEntry">Confirm on Pharmacy's Behalf</button>
        <button class="filter-select" style="cursor:pointer;" @click="dismissOtpBanner">Dismiss</button>
        <span v-if="otpError" style="color:var(--status-red);font-size:12.5px;">{{ otpError }}</span>
      </div>
    </div>

    <div class="card">
      <div class="toolbar">
        <input class="search-input" v-model="search" placeholder="Search order no. or pharmacy…" />
        <select class="filter-select" v-model="statusFilter"><option v-for="s in statusOptions" :key="s">{{ s }}</option></select>
        <span class="result-count">{{ filtered.length }} of {{ orders.length }} shown</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Order No.</th><th>Pharmacy</th><th>Products</th><th>Value</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="o in filtered" :key="o.orderNo" class="clickable" @click="openDrawer(o)">
              <td class="cell-strong">{{ o.orderNo }}</td><td>{{ o.pharmacyName }}</td>
              <td>{{ o.lineItems[0].productName }}<span v-if="o.lineItems.length>1" class="cell-muted"> +{{ o.lineItems.length-1 }} more</span></td>
              <td class="cell-muted">${{ o.orderValue.toLocaleString() }}</td>
              <td><StatusPill :status="o.currentStage" :label="o.currentStage" /></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <DetailDrawer :open="!!drawerOrder" :title="drawerOrder?.orderNo || ''" @close="drawerOrderNo=null">
      <template v-if="drawerOrder">
        <div class="oos-meta-grid" style="margin-bottom:18px;">
          <div><div class="label">Pharmacy</div>{{ drawerOrder.pharmacyName }}</div>
          <div><div class="label">Order Date</div>{{ drawerOrder.orderDate }}</div>
          <div><div class="label">Total Value</div>${{ drawerOrder.orderValue.toLocaleString() }}</div>
        </div>
        <table class="data-table" style="margin-bottom:18px;">
          <thead><tr><th>Product</th><th>Ordered</th><th>Free</th><th>Total Delivered</th><th>Line Value</th></tr></thead>
          <tbody>
            <tr v-for="li in drawerOrder.lineItems" :key="li.productSku">
              <td class="cell-strong">{{ li.productName }} <span v-if="li.schemeApplied" class="tier-badge Gold">10+1</span></td>
              <td class="cell-muted">{{ li.quantity }}</td>
              <td class="cell-muted">{{ li.freeQuantity || 0 }}</td>
              <td class="cell-strong">{{ li.totalQuantity || li.quantity }}</td>
              <td class="cell-muted">${{ li.lineValue.toLocaleString() }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="!isLateral" class="eyebrow" style="margin-bottom:10px;">Status Timeline</div>
        <div v-if="!isLateral" class="stepper">
          <div v-for="(s,i) in STAGES" :key="s" class="stepper-step">
            <div class="stepper-marker">
              <div class="stepper-dot" :class="{ done: i<stageIdx, current: i===stageIdx }"></div>
              <div v-if="i<STAGES.length-1" class="stepper-line" :class="{ done: i<stageIdx }"></div>
            </div>
            <div class="stepper-label" :class="i===stageIdx?'current':(i<stageIdx?'':'pending')">
              {{ s }}
              <div v-if="drawerOrder.statusHistory[i]" class="cell-muted" style="font-size:10.5px;">{{ new Date(drawerOrder.statusHistory[i].timestamp).toLocaleString() }}</div>
            </div>
          </div>
        </div>
        <div v-if="drawerOrder.currentStage==='Cancelled'" class="oos-remarks" style="margin-top:16px;">{{ drawerOrder.cancelReason || '—' }}</div>
        <div v-if="drawerOrder.comments && drawerOrder.comments.length" style="margin-top:16px;">
          <div class="eyebrow" style="margin-bottom:8px;">Comments</div>
          <div v-for="(c,i) in drawerOrder.comments" :key="i" class="oos-remarks" style="margin-bottom:8px;">
            <strong style="display:block;font-size:11px;color:var(--color-text-muted);">{{ c.from }} — {{ new Date(c.timestamp).toLocaleString() }}</strong>{{ c.text }}
          </div>
          <button v-if="drawerOrder.currentStage==='Returned for Modification'" class="btn-primary" style="margin-top:8px;" @click="store.salesExecV2.resubmit(drawerOrder.orderNo)">Resubmit</button>
        </div>
      </template>
    </DetailDrawer>

    <DetailDrawer :open="showNewOrder" title="New Order" @close="showNewOrder=false">
      <div class="form-group">
        <label>Pharmacy</label>
        <select class="form-select" v-model="form.customerId">
          <option value="" disabled>Select a pharmacy…</option>
          <option v-for="p in pharmacies" :key="p.customerId" :value="p.customerId">{{ p.pharmacyName }}</option>
        </select>
      </div>
      <div v-if="selectedPharmacy" style="margin-bottom:16px;">
        <span class="status-pill" :class="creditUsedPct>=90?'red':creditUsedPct>=70?'yellow':'green'"><span class="dot"></span>Credit Limit {{ creditUsedPct }}% Used</span>
      </div>

      <div v-for="(line,i) in form.lines" :key="i" style="margin-bottom:14px;">
        <div style="display:grid;grid-template-columns:2fr 0.8fr 0.8fr 0.9fr auto;gap:8px;align-items:end;">
          <div class="form-group" style="margin:0;">
            <label>Product</label>
            <select class="form-select" v-model="line.productSku">
              <option value="" disabled>Select…</option>
              <optgroup label="With 10+1 scheme">
                <option v-for="p in availableSchemeProducts(i)" :key="p.sku" :value="p.sku">{{ p.productName }}</option>
              </optgroup>
              <optgroup label="Without scheme">
                <option v-for="p in availableNonSchemeProducts(i)" :key="p.sku" :value="p.sku">{{ p.productName }}</option>
              </optgroup>
            </select>
          </div>
          <div class="form-group" style="margin:0;">
            <label>Qty</label>
            <input class="form-input" type="number" min="1" v-model.number="line.quantity" />
          </div>
          <!-- Total Qty: quantity ordered + free units, read-only -->
          <div class="form-group" style="margin:0;">
            <label>Total Qty</label>
            <input class="form-input" style="background:var(--color-border-light,#f2f1ec);" readonly
                   :value="lineDetail(line) ? lineDetail(line).totalQuantity : '—'" />
          </div>
          <!-- Line Price: quantity x unit price, billed amount for this line, read-only -->
          <div class="form-group" style="margin:0;">
            <label>Line Price</label>
            <input class="form-input" style="background:var(--color-border-light,#f2f1ec);" readonly
                   :value="lineDetail(line) ? '$'+lineDetail(line).value.toLocaleString() : '—'" />
          </div>
          <button class="filter-select" style="cursor:pointer;height:38px;" @click="removeLine(i)" :disabled="form.lines.length===1">×</button>
        </div>
        <div v-if="line.productSku && productHasScheme(line.productSku)" style="margin-top:6px;">
          <span class="tier-badge Gold">10+1 offer</span>
          <span v-if="lineDetail(line) && lineDetail(line).freeQuantity > 0" class="cell-muted" style="font-size:11.5px;margin-left:8px;">
            {{ line.quantity }} ordered + {{ lineDetail(line).freeQuantity }} free = {{ lineDetail(line).totalQuantity }} delivered
          </span>
        </div>
      </div>
      <button class="filter-select" style="cursor:pointer;margin-bottom:16px;" @click="addLine">+ Add Product</button>

      <div class="oos-remarks" style="margin-bottom:16px;">
        <strong style="display:block;font-size:11px;text-transform:uppercase;color:var(--color-text-muted);">Order Total</strong>
        ${{ orderTotal.toLocaleString() }}
      </div>
      <button class="btn-primary" :disabled="!canSubmit" @click="placeOrder">Place Order</button>
    </DetailDrawer>
  </div>
</template>
